import { createPortal } from 'react-dom';
import type { ChangeEvent, RefObject } from 'react';
import { PhotoPreview } from '@/components/PhotoPreview';
import { PhotoProgress } from '@/components/PhotoProgress';
import { PhotoProcessingOverlay, type WaitingForLocation } from '@/components/PhotoProcessingOverlay';
import { CheckConfirmationDialog } from '@/components/CheckConfirmationDialog';
import { InPageCamera, type InPageCameraErrorPhase } from '@/components/InPageCamera';
import type { PendingCheckData, UploadStage } from '@/hooks/usePhotoUpload';

interface ModalsContainerProps {
  // In-page camera
  showViewfinder: boolean;
  onViewfinderShot: (photo: Blob) => void;
  onViewfinderError: (error: unknown, phase: InPageCameraErrorPhase) => void;
  onViewfinderBack: () => void;

  // Native camera
  cameraInputRef: RefObject<HTMLInputElement | null>;
  onCameraInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  processingPhoto: boolean;
  waitingForLocation: WaitingForLocation;
  cameraError: string | null;
  onCameraErrorClose: () => void;
  /** Camera error button opens the camera again. */
  cameraErrorRecovers?: boolean;

  // In-page photo preview
  /** capturedPhoto is an in-page photo waiting for Confirm; nothing is uploaded yet. */
  awaitingApproval: boolean;
  onPhotoConfirm: () => void;
  onPhotoRetake: () => void;

  // Upload progress
  capturedPhoto: Blob | null;
  uploadStage: UploadStage;
  r2PhotoUrl: string | null;
  error: string | null;
  onCapturePhoto: () => void;
  onPhotoBack: () => void;

  // Check Confirmation Dialog
  showCheckConfirmation: boolean;
  pendingCheckData: PendingCheckData | null;
  confirming: boolean;
  expectedVin?: string | null;
  onCheckConfirm: () => Promise<void>;
  onCheckRetake: () => void;
}

/**
 * Container component for all modals in the photo capture workflow
 * Centralizes modal rendering logic to simplify parent component
 */
export function ModalsContainer({
  showViewfinder,
  onViewfinderShot,
  onViewfinderError,
  onViewfinderBack,
  cameraInputRef,
  onCameraInputChange,
  processingPhoto,
  waitingForLocation,
  cameraError,
  onCameraErrorClose,
  cameraErrorRecovers = false,
  // In-page photo preview
  awaitingApproval,
  onPhotoConfirm,
  onPhotoRetake,
  // Upload progress
  capturedPhoto,
  uploadStage,
  r2PhotoUrl,
  error,
  onCapturePhoto,
  onPhotoBack,
  // Check Confirmation
  showCheckConfirmation,
  pendingCheckData,
  confirming,
  expectedVin,
  onCheckConfirm,
  onCheckRetake,
}: ModalsContainerProps) {
  return (
    <>
      {/* Outside the rotated portrait shell. display:none makes Android Chrome drop the file. */}
      {typeof document !== 'undefined' &&
        createPortal(
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="camera-file-input"
            onChange={onCameraInputChange}
            data-testid="native-camera-input"
          />,
          document.body
        )}

      {/* Upload and validation progress, or the error with Capture photo.
          The local JPEG is dropped once R2 is showing, so the screen stays up from the URL. */}
      {!awaitingApproval && (capturedPhoto || (r2PhotoUrl && (uploadStage !== 'idle' || error))) && (
        <PhotoProgress
          photo={r2PhotoUrl ? null : capturedPhoto}
          stage={uploadStage}
          r2PhotoUrl={r2PhotoUrl}
          error={error}
          onCapture={onCapturePhoto}
          onBack={onPhotoBack}
        />
      )}

      {/* Before the viewfinder, so a retake opens over the photo. */}
      {awaitingApproval && capturedPhoto && (
        <PhotoPreview photo={capturedPhoto} onConfirm={onPhotoConfirm} onRetake={onPhotoRetake} />
      )}

      {/* Over the illustration or the old photo, which stay up behind it like behind the native camera. */}
      {showViewfinder && (
        <InPageCamera onShot={onViewfinderShot} onError={onViewfinderError} onBack={onViewfinderBack} />
      )}

      {/* After the progress screen so it covers the old photo while a retake is processed. */}
      {(processingPhoto || cameraError) && (
        <PhotoProcessingOverlay
          waitingForLocation={waitingForLocation}
          error={cameraError}
          onClose={onCameraErrorClose}
          recover={cameraErrorRecovers}
        />
      )}

      {/* Check Confirmation Dialog */}
      {showCheckConfirmation && pendingCheckData && (
        <CheckConfirmationDialog
          checkResult={pendingCheckData.checkResult}
          expectedVin={expectedVin}
          onConfirm={onCheckConfirm}
          onRetake={onCheckRetake}
          confirming={confirming}
        />
      )}
    </>
  );
}
